import { BottomArrow } from "@/Icon/BottomArrow";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import MessageModal from "@/MessageModal";
import { useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { format, parse } from "date-fns";
import {
  useBusinessMypageUsageHistory,
  type BusinessMypageUsageHistoryItem,
} from "~/services/business/myPage";

const PRESET_MONTHS = ["1", "3", "6", "12"] as const;
type PeriodPreset = (typeof PRESET_MONTHS)[number];

function formatDisplayDate(ymd: string) {
  if (!ymd) return "";
  return dayjs(ymd).format("YYYY.MM.DD");
}

const getDefaultRange = () => {
  const t = dayjs();
  return {
    start: t.startOf("month").format("YYYY-MM-DD"),
    end: t.format("YYYY-MM-DD"),
  };
};

const UsageHistory = () => {
  const defaultRange = useMemo(getDefaultRange, []);

  const [expanded, setExpanded] = useState(false);
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>("1");
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [searchKeyword, setSearchKeyword] = useState("");
  // 조회 버튼 클릭 시 반영·API 호출. 초기값은 디폴트 조건으로 첫 진입 시 자동 조회
  const [appliedParams, setAppliedParams] = useState<{
    searchKeyword?: string;
    startDate: string;
    endDate: string;
  }>(() => ({
    startDate: defaultRange.start,
    endDate: defaultRange.end,
  }));
  // 조회 클릭 시마다 변경해 같은 조건이어도 항상 재호출
  const [refetchKey, setRefetchKey] = useState(0);
  const [isStartDateModalOpen, setIsStartDateModalOpen] = useState(false);
  const [isEndDateModalOpen, setIsEndDateModalOpen] = useState(false);
  // 달력으로 직접 날짜 선택했을 때는 프리셋(1/3/6/12개월) 아무것도 선택 안 된 상태
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);

  const apiParams = useMemo(
    () => ({
      searchKeyword: appliedParams.searchKeyword,
      startDate: appliedParams.startDate,
      endDate: appliedParams.endDate,
    }),
    [
      appliedParams.searchKeyword,
      appliedParams.startDate,
      appliedParams.endDate,
    ]
  );

  const { data, isLoading, error } = useBusinessMypageUsageHistory(apiParams, {
    refetchKey,
  });

  // 요약 문구: "1개월 · 전체 · 최신순" (조회한 기준으로 표시. 직접 선택 시 "직접 선택")
  const summaryText = useMemo(() => {
    const period = isCustomDateRange ? "직접 선택" : `${periodPreset}개월`;
    const keywordPart = appliedParams.searchKeyword ? "검색어" : "전체";
    return `${period} · ${keywordPart} · 최신순`;
  }, [periodPreset, appliedParams.searchKeyword, isCustomDateRange]);

  const applyPreset = useCallback((preset: PeriodPreset) => {
    setIsCustomDateRange(false);
    setPeriodPreset(preset);
    const today = dayjs();
    const months = Number.parseInt(preset, 10);
    const end = today.format("YYYY-MM-DD");
    const start =
      months === 1
        ? today.startOf("month").format("YYYY-MM-DD")
        : today.subtract(months, "month").format("YYYY-MM-DD");
    setEndDate(end);
    setStartDate(start);
  }, []);

  const handleStartDateSelect = (day: Date | null) => {
    if (day) {
      setIsCustomDateRange(true);
      const value = format(day, "yyyy-MM-dd");
      setStartDate(value);
      if (endDate && dayjs(value).isAfter(dayjs(endDate))) {
        setEndDate(value);
      }
      setIsStartDateModalOpen(false);
    }
  };

  const handleEndDateSelect = (day: Date | null) => {
    if (day) {
      setIsCustomDateRange(true);
      const value = format(day, "yyyy-MM-dd");
      setEndDate(value);
      if (startDate && dayjs(value).isBefore(dayjs(startDate))) {
        setStartDate(value);
      }
      setIsEndDateModalOpen(false);
    }
  };

  const handleSearch = () => {
    setAppliedParams({
      searchKeyword: searchKeyword.trim() || undefined,
      startDate,
      endDate,
    });
    setRefetchKey((k) => k + 1);
  };

  // 검색 조건만 초기화. 리스트는 그대로 두고, 조회 버튼을 눌렀을 때만 API 호출
  const handleReset = () => {
    setIsCustomDateRange(false);
    setPeriodPreset("1");
    setStartDate(defaultRange.start);
    setEndDate(defaultRange.end);
    setSearchKeyword("");
  };

  const list: BusinessMypageUsageHistoryItem[] = data?.data ?? [];

  return (
    <div className="p-4 bg-gray-50">
      {/* 검색 영역 - 접이식 */}
      <div className="overflow-hidden mx-auto max-w-md bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* 접힌 상태: 요약 + 날짜 범위 (클릭 시 펼침) */}
        <button
          type="button"
          className="flex justify-between items-center px-4 py-3 w-full text-left bg-white border-b border-gray-100"
          onClick={() => setExpanded((e) => !e)}
        >
          <span className="text-sm text-gray-700">{summaryText}</span>
          <span
            className={`text-gray-500 transition-transform ${expanded ? "rotate-180" : ""}`}
          >
            <BottomArrow />
          </span>
        </button>
        <div className="px-4 py-2.5 bg-gray-50">
          <span className="text-sm text-gray-600">
            {formatDisplayDate(appliedParams.startDate)} ~{" "}
            {formatDisplayDate(appliedParams.endDate)}
          </span>
        </div>

        {/* 펼친 상태: 상세 필터 */}
        {expanded && (
          <div className="px-4 py-4 space-y-4 border-t border-gray-100 bg-gray-50/50">
            {/* 조회 기간 */}
            <div>
              <p className="mb-2 text-xs font-medium text-gray-500">
                조회 기간
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {PRESET_MONTHS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border
                      ${
                        !isCustomDateRange && periodPreset === preset
                          ? "bg-orange-400 text-white border-orange-400"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-orange-50"
                      }`}
                  >
                    {preset}개월
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <button
                  type="button"
                  onClick={() => setIsStartDateModalOpen(true)}
                  className="flex-1 min-w-[120px] px-3 py-2 text-sm text-left border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                >
                  {formatDisplayDate(startDate)}
                </button>
                <span className="text-gray-400">~</span>
                <button
                  type="button"
                  onClick={() => setIsEndDateModalOpen(true)}
                  className="flex-1 min-w-[120px] px-3 py-2 text-sm text-left border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                >
                  {formatDisplayDate(endDate)}
                </button>
              </div>
            </div>

            {/* 키워드(선택) */}
            <div>
              <p className="mb-2 text-xs font-medium text-gray-500">
                키워드(선택)
              </p>
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="반려동물 이름 또는 보호자 닉네임"
                className="px-3 py-2 w-full text-sm placeholder-gray-400 bg-white rounded-lg border border-gray-300"
              />
            </div>

            {/* 초기화 / 조회 */}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={handleReset}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
              >
                <span className="text-base" aria-hidden>
                  ↺
                </span>{" "}
                초기화
              </button>
              <button
                type="button"
                onClick={handleSearch}
                className="flex-1 py-2.5 rounded-lg bg-orange-400 text-white text-sm font-medium"
              >
                조회
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 시작일 선택 달력 모달 */}
      <MessageModal
        isOpen={isStartDateModalOpen}
        onRequestClose={() => setIsStartDateModalOpen(false)}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none animate-scaleIn"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          disablePast={false}
          dayOnClick={handleStartDateSelect}
          selectedDay={
            startDate ? parse(startDate, "yyyy-MM-dd", new Date()) : null
          }
          schedules={{}}
        />
        <div className="flex gap-3 px-4 pt-2">
          <button
            type="button"
            onClick={() => setIsStartDateModalOpen(false)}
            className="flex-1 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
          >
            취소
          </button>
          <button
            type="button"
            onClick={() => setIsStartDateModalOpen(false)}
            className="flex-1 py-2 text-sm font-semibold text-white bg-orange-400 rounded-full"
          >
            확인
          </button>
        </div>
      </MessageModal>

      {/* 종료일 선택 달력 모달 */}
      <MessageModal
        isOpen={isEndDateModalOpen}
        onRequestClose={() => setIsEndDateModalOpen(false)}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none animate-scaleIn"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          disablePast={false}
          minDate={
            startDate ? parse(startDate, "yyyy-MM-dd", new Date()) : undefined
          }
          maxDate={new Date()}
          dayOnClick={handleEndDateSelect}
          selectedDay={
            endDate ? parse(endDate, "yyyy-MM-dd", new Date()) : null
          }
          schedules={{}}
        />
        <div className="flex gap-3 px-4 pt-2">
          <button
            type="button"
            onClick={() => setIsEndDateModalOpen(false)}
            className="flex-1 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
          >
            취소
          </button>
          <button
            type="button"
            onClick={() => setIsEndDateModalOpen(false)}
            className="flex-1 py-2 text-sm font-semibold text-white bg-orange-400 rounded-full"
          >
            확인
          </button>
        </div>
      </MessageModal>

      {/* 리스트 영역 */}
      <div className="max-w-md mx-auto mt-3 rounded-lg border border-gray-200 bg-white min-h-[400px] overflow-hidden">
        {isLoading && (
          <div className="py-10 text-sm text-center text-gray-500">
            조회 중...
          </div>
        )}
        {error && (
          <div className="py-10 text-sm text-center text-red-500">
            이용내역을 불러오지 못했습니다.
          </div>
        )}
        {!isLoading && !error && list.length === 0 && (
          <div className="py-10 text-sm text-center text-gray-500">
            이용내역이 없습니다.
          </div>
        )}
        {!isLoading && !error && list.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {list.map((item) => (
              <li
                key={item.reservationSeq}
                className="flex gap-3 items-center px-4 py-3"
              >
                <div className="overflow-hidden w-12 h-12 bg-gray-100 rounded-full shrink-0">
                  {item.fileUrl ? (
                    <img
                      src={item.fileUrl}
                      alt={item.petName}
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <div className="flex justify-center items-center w-full h-full text-xs text-gray-400">
                      N
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.petName} · {item.userName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {item.productName}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {[
                      item.petWeight && `몸무게 ${item.petWeight}`,
                      item.ticketName && `이용권 ${item.ticketName}`,
                      item.paymentType && `결제구분 ${item.paymentType}`,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "-"}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UsageHistory;
